import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { BehaviorSubject } from "rxjs";
import { CfsTemplateBaseComponent, CfsTemplateError, CfsTemplateErrorData, TemplateAnimationType } from "clearline-common";
import { CfsTemplateErrorDataFields, CfsTemplateErrorTypes, generateCommonCfsTemplateError } from "../cfs-template-errors";

@Component({
  selector: "lib-generated-html-template",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./generated-html-template.component.html",
  styleUrls: ["./generated-html-template.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeneratedHtmlTemplateComponent implements CfsTemplateBaseComponent, OnInit, AfterViewInit {
  @Input() generatedBody: SafeHtml = "";
  @Input() isPrintView = false;
  @Input() animationType = TemplateAnimationType.Animated;

  @Output() ready = new EventEmitter();
  @Output() error = new EventEmitter<CfsTemplateError>();

  protected animated$ = new BehaviorSubject<boolean>(false);
  private eventListeners: Array<() => void> = [];
  private totalMediaElements = 0;
  private loadedMediaElements = 0;
  private failedMediaElements = 0;
  private initialReadyEmitted = false;

  constructor(private elementRef: ElementRef, private renderer: Renderer2, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    if (this.generatedBody) {
      if (this.animationType === TemplateAnimationType.Animated) {
        this.animated$.next(true);
      }
    } else {
      this.setError(CfsTemplateErrorTypes.EmptyData);
    }
  }

  ngAfterViewInit(): void {
    if (this.generatedBody) {
      this.attachMediaEventHandlers();

      if (this.totalMediaElements === 0) {
        this.emitReady();
      }
    }
  }

  play(): void {
    this.playAnimation();
  }

  stop(): void {
    this.stopAnimation();
  }

  pause(): void {
    this.stopAnimation();
  }

  restart(): void {
    this.playAnimation();
  }

  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private playAnimation(): void {
    this.animated$.next(
      this.animationType === TemplateAnimationType.Animated || this.animationType === TemplateAnimationType.OnDemandAnimated
    );
  }

  private stopAnimation(): void {
    this.animated$.next(false);
  }

  private emitReady(): void {
    if (!this.initialReadyEmitted) {
      this.initialReadyEmitted = true;
      this.ready.emit();
    }
  }

  private mediaElementLoaded(): void {
    this.loadedMediaElements++;
    this.checkAllMediaLoaded();
  }

  private mediaElementFailed(errorName: CfsTemplateErrorTypes, errorMessage: string, url: string = ""): void {
    const errorData: CfsTemplateErrorData = { field: CfsTemplateErrorDataFields.Url, value: url };

    this.failedMediaElements++;
    this.setError(errorName, errorMessage, errorData);
    this.checkAllMediaLoaded();
  }

  private setError(errorName: CfsTemplateErrorTypes, errorMessage = "", errorData: CfsTemplateErrorData | null = null): void {
    const templateError: CfsTemplateError = generateCommonCfsTemplateError(errorName, errorMessage, errorData);

    this.error.emit(templateError);
  }

  private checkAllMediaLoaded(): void {
    if (this.loadedMediaElements + this.failedMediaElements === this.totalMediaElements && this.failedMediaElements === 0) {
      this.emitReady();
    }
  }

  private attachMediaEventHandlers(): void {
    // Find all images:
    const images = this.elementRef.nativeElement.querySelectorAll("img");
    images.forEach((img: HTMLImageElement) => {
      this.totalMediaElements++;

      if (!img.src || img.src === window.location.origin + "/") {
        this.mediaElementFailed(CfsTemplateErrorTypes.EmptySrc, "");
        return;
      }

      const loadUnregister = this.renderer.listen(img, "load", () => {
        this.mediaElementLoaded();
      });
      this.eventListeners.push(loadUnregister);

      const errorUnregister = this.renderer.listen(img, "error", () => {
        const src = img.src || "unknown";
        this.mediaElementFailed(CfsTemplateErrorTypes.ImageLoad, src);
      });
      this.eventListeners.push(errorUnregister);

      if (img.complete) {
        if (img.naturalWidth === 0) {
          const src = img.src || "unknown";
          this.mediaElementFailed(CfsTemplateErrorTypes.ImageLoad, src);
        } else {
          this.mediaElementLoaded();
        }
      }
    });

    // Find all videos:
    const videos = this.elementRef.nativeElement.querySelectorAll("video");
    videos.forEach((video: HTMLVideoElement) => {
      this.totalMediaElements++;

      if (!video.src || video.src === window.location.origin + "/") {
        this.mediaElementFailed(CfsTemplateErrorTypes.EmptySrc, "");
        return;
      }

      const loadUnregister = this.renderer.listen(video, "loadeddata", () => {
        this.mediaElementLoaded();
      });
      this.eventListeners.push(loadUnregister);

      const errorUnregister = this.renderer.listen(video, "error", () => {
        const src = video.currentSrc || video.src || "unknown";
        this.mediaElementFailed(CfsTemplateErrorTypes.VideoLoad, src);
      });
      this.eventListeners.push(errorUnregister);

      if (video.readyState >= 2) {
        this.mediaElementLoaded();
      }
    });
  }

  private removeEventHandlers(): void {
    this.eventListeners.forEach((unregister) => unregister());
    this.eventListeners = [];
  }
}
